import { useEffect, useState } from 'react'
import { AutoComplete } from 'antd'
import { useTranslation } from 'react-i18next'
import { searchInstallers, type Installer } from './profileApi'

export interface InstallerValue {
  personId: string | null
  name: string
}

interface InstallerInputProps {
  value?: InstallerValue | null
  onChange?: (value: InstallerValue | null) => void
  disabled?: boolean
}

function describe(p: Installer): string {
  return [p.fullName, p.jobTitleName, p.orgNodeName].filter(Boolean).join(' · ')
}

export function InstallerInput({ value, onChange, disabled }: InstallerInputProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(value?.name ?? '')
  const [options, setOptions] = useState<Installer[]>([])

  useEffect(() => {
    setText(value?.name ?? '')
  }, [value?.name])

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      searchInstallers(text, controller.signal)
        .then(setOptions)
        .catch(() => setOptions([]))
    }, 300)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [text])

  return (
    <AutoComplete
      value={text}
      disabled={disabled}
      placeholder={t('customer.profile.installedByPlaceholder')}
      options={options.map((p) => ({ value: p.id, label: describe(p) }))}
      onSearch={(next) => {
        setText(next)
        const name = next.trim()
        onChange?.(name ? { personId: null, name } : null)
      }}
      onSelect={(id: string) => {
        const person = options.find((p) => p.id === id)
        if (!person) return
        setText(person.fullName)
        onChange?.({ personId: person.id, name: person.fullName })
      }}
      allowClear
      onClear={() => {
        setText('')
        onChange?.(null)
      }}
    />
  )
}
